import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import {
  ArrowLeft,
  Key,
  CurrencyDollar,
  ListChecks,
  Eye,
  EyeSlash,
  Trash,
  Plus,
  ArrowsClockwise,
  Webhook,
  Lightning,
  CheckCircle,
  Warning,
} from "@phosphor-icons/react";

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [apiKeys, setApiKeys] = useState({});
  const [tradingPairs, setTradingPairs] = useState([]);
  const [availablePairs, setAvailablePairs] = useState([]);
  const [loading, setLoading] = useState(true);

  // API Key Form State
  const [keyForm, setKeyForm] = useState({
    exchange: "binance",
    api_key: "",
    api_secret: "",
    is_testnet: true,
  });
  const [showSecret, setShowSecret] = useState(false);
  const [savingKey, setSavingKey] = useState(false);

  // Webhook Form State
  const [webhookConfig, setWebhookConfig] = useState(null);
  const [webhookForm, setWebhookForm] = useState({
    webhook_url: "",
    exchange: "bybit",
    enabled: true,
  });
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);

  // Manual Balance State
  const [manualBalances, setManualBalances] = useState({});
  const [bybitBalance, setBybitBalance] = useState("");
  const [savingBalance, setSavingBalance] = useState(false);
  const [fetchingBalance, setFetchingBalance] = useState(false);
  const [lastFetchedBalance, setLastFetchedBalance] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [keysRes, pairsRes, availableRes, webhookRes, manualBalRes] = await Promise.all([
        api.get("/user/api-keys"),
        api.get("/user/trading-pairs"),
        api.get("/trading/available-pairs"),
        api.get("/user/webhook-config").catch(() => ({ data: { configured: false } })),
        api.get("/user/manual-balance").catch(() => ({ data: { balances: {} } })),
      ]);
      setApiKeys(keysRes.data);
      setTradingPairs(pairsRes.data.pairs || []);
      setAvailablePairs(availableRes.data.pairs || []);
      setWebhookConfig(webhookRes.data);
      setManualBalances(manualBalRes.data.balances || {});
      if (manualBalRes.data.balances?.bybit?.balance) {
        setBybitBalance(manualBalRes.data.balances.bybit.balance.toString());
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveApiKey = async (e) => {
    e.preventDefault();
    if (!keyForm.api_key || !keyForm.api_secret) {
      toast.error("Both API Key and Secret are required");
      return;
    }

    setSavingKey(true);
    try {
      await api.post("/user/api-keys", keyForm);
      toast.success("API keys saved");
      setKeyForm({ ...keyForm, api_key: "", api_secret: "" });
      fetchSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save API keys");
    } finally {
      setSavingKey(false);
    }
  };

  const handleDeleteApiKey = async (exchange, isTestnet) => {
    if (!window.confirm(`Delete ${exchange} ${isTestnet ? "testnet" : "live"} API keys?`)) {
      return;
    }

    try {
      await api.delete(`/user/api-keys/${exchange}?is_testnet=${isTestnet}`);
      toast.success("API keys removed");
      fetchSettings();
    } catch (e) {
      toast.error("Failed to remove API keys");
    }
  };

  const handlePairToggle = async (pair) => {
    const newPairs = tradingPairs.includes(pair)
      ? tradingPairs.filter((p) => p !== pair)
      : [...tradingPairs, pair];

    try {
      await api.put("/user/trading-pairs", { pairs: newPairs });
      setTradingPairs(newPairs);
      toast.success("Trading pairs updated");
    } catch (e) {
      toast.error("Failed to update trading pairs");
    }
  };

  const handleResetBalance = async () => {
    if (!window.confirm("Reset your paper trading balance to $1,000?")) {
      return;
    }

    try {
      await api.post("/user/reset-balance");
      await refreshUser();
      toast.success("Balance reset to $1,000");
    } catch (e) {
      toast.error("Failed to reset balance");
    }
  };

  // Webhook handlers
  const handleSaveWebhook = async (e) => {
    e.preventDefault();
    if (!webhookForm.webhook_url) {
      toast.error("Webhook URL is required");
      return;
    }

    setSavingWebhook(true);
    try {
      await api.put("/user/webhook-config", webhookForm);
      toast.success("Webhook configured successfully");
      fetchSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save webhook");
    } finally {
      setSavingWebhook(false);
    }
  };

  const handleDeleteWebhook = async () => {
    if (!window.confirm("Remove webhook configuration?")) {
      return;
    }

    try {
      await api.delete("/user/webhook-config");
      toast.success("Webhook removed");
      setWebhookConfig({ configured: false });
      setWebhookForm({ webhook_url: "", exchange: "bybit", enabled: true });
    } catch (e) {
      toast.error("Failed to remove webhook");
    }
  };

  const handleTestWebhook = async () => {
    setTestingWebhook(true);
    try {
      const res = await api.post("/trading/webhook-test");
      if (res.data.success) {
        toast.success("Webhook connection successful!");
      } else {
        toast.error(`Webhook test failed: ${res.data.message}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Webhook test failed");
    } finally {
      setTestingWebhook(false);
    }
  };

  const handleSaveManualBalance = async (e) => {
    e.preventDefault();
    const balanceValue = parseFloat(bybitBalance);
    if (isNaN(balanceValue) || balanceValue < 0) {
      toast.error("Please enter a valid balance amount");
      return;
    }

    setSavingBalance(true);
    try {
      await api.put("/user/manual-balance", {
        exchange: "bybit",
        balance: balanceValue,
      });
      toast.success(`Bybit balance set to $${balanceValue.toFixed(2)}`);
      fetchSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save balance");
    } finally {
      setSavingBalance(false);
    }
  };

  const handleFetchBalance = async () => {
    setFetchingBalance(true);
    try {
      const res = await api.post("/trading/fetch-balance");
      console.log("Fetch balance response:", res.data);
      
      if (res.data.success) {
        const balance = res.data.balance?.free || res.data.balance?.total || 0;
        const allBalances = res.data.all_balances || {};
        const rawText = res.data.raw_text || '';
        
        setLastFetchedBalance({
          balance: balance,
          all_balances: allBalances,
          fetched_at: new Date().toISOString(),
          raw_response: res.data.raw_response,
          raw_text: rawText,
          stored: res.data.stored
        });
        
        if (balance > 0) {
          setBybitBalance(balance.toString());
          toast.success(`Balance updated: $${balance.toLocaleString('en-US', {minimumFractionDigits: 2})}`);
          // Refresh settings to show updated balance
          fetchSettings();
        } else if (rawText && rawText !== 'Created') {
          // TTA returned something but we couldn't parse USDT
          toast.info(`TTA Response received. Check if USDT balance is included.`);
        } else {
          // TTA returned "Created" - request is queued
          toast.warning("Request queued by TTA. Try again in a few seconds.");
        }
      } else {
        toast.error(res.data.error || "Failed to fetch balance");
      }
    } catch (e) {
      console.error("Fetch balance error:", e);
      toast.error(e.response?.data?.detail || "Failed to fetch balance from webhook");
    } finally {
      setFetchingBalance(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F0]">
        <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F4F0]">
      {/* Header */}
      <header className="bg-white border-b border-[#111111]/10 sticky top-0 z-50">
        <div className="max-w-[1200px] mx-auto px-4 h-14 flex items-center gap-4">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/dashboard")}
            className="p-2 hover:bg-[#F4F4F0] rounded transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-bold text-lg tracking-tight">Settings</h1>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto p-4 space-y-6">
        {/* API Keys Section */}
        <div className="bg-white border border-[#111111]/10" data-testid="api-keys-section">
          <div className="panel-header flex items-center gap-2">
            <Key size={18} />
            <span className="font-medium">API Keys</span>
          </div>
          <div className="p-4">
            {/* Current Keys */}
            <div className="mb-6">
              <p className="data-label mb-3">Configured Keys</p>
              <div className="space-y-2">
                {Object.entries(apiKeys).length > 0 ? (
                  Object.entries(apiKeys).map(([keyType, data]) => (
                    <div
                      key={keyType}
                      className="flex items-center justify-between p-3 bg-[#F4F4F0] border border-[#111111]/10"
                    >
                      <div>
                        <p className="font-medium text-sm">{keyType.replace("_", " ").toUpperCase()}</p>
                        <p className="text-xs text-[#555555] font-mono">{data.api_key}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2 py-0.5 text-xs ${
                            data.configured
                              ? "bg-[#00C853]/10 text-[#00C853]"
                              : "bg-[#FF3B30]/10 text-[#FF3B30]"
                          }`}
                        >
                          {data.configured ? "ACTIVE" : "INCOMPLETE"}
                        </span>
                        <button
                          data-testid={`delete-key-${keyType}`}
                          onClick={() => handleDeleteApiKey(
                            keyType.includes("binance") ? "binance" : "bybit",
                            keyType.includes("testnet")
                          )}
                          className="p-1.5 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-[#555555]">No API keys configured</p>
                )}
              </div>
            </div>

            {/* Add New Key Form */}
            <div>
              <p className="data-label mb-3">Add New Key</p>
              
              {/* Exchange Note */}
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-xs text-amber-800">
                <strong>Note:</strong> Bybit API may be geo-restricted in some regions. If you experience connection errors with Bybit, try using Binance instead. Both exchanges support testnet for paper trading.
              </div>
              
              <form onSubmit={handleSaveApiKey} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="data-label block mb-2">Exchange</label>
                    <select
                      data-testid="exchange-select"
                      value={keyForm.exchange}
                      onChange={(e) => setKeyForm({ ...keyForm, exchange: e.target.value })}
                      className="w-full h-10 px-3 border border-[#111111]/10 bg-white font-mono text-sm focus:border-[#002FA7]"
                    >
                      <option value="binance">Binance</option>
                      <option value="bybit">Bybit</option>
                    </select>
                  </div>
                  <div className="flex items-end gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        data-testid="testnet-checkbox"
                        checked={keyForm.is_testnet}
                        onCheckedChange={(checked) =>
                          setKeyForm({ ...keyForm, is_testnet: checked })
                        }
                      />
                      <span className="text-sm">Testnet / Paper Trading</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="data-label block mb-2">API Key</label>
                  <Input
                    data-testid="api-key-input"
                    type="text"
                    value={keyForm.api_key}
                    onChange={(e) => setKeyForm({ ...keyForm, api_key: e.target.value })}
                    placeholder="Enter API key"
                    className="w-full h-10 border-[#111111]/10 rounded-none font-mono text-sm"
                  />
                </div>

                <div>
                  <label className="data-label block mb-2">API Secret</label>
                  <div className="relative">
                    <Input
                      data-testid="api-secret-input"
                      type={showSecret ? "text" : "password"}
                      value={keyForm.api_secret}
                      onChange={(e) => setKeyForm({ ...keyForm, api_secret: e.target.value })}
                      placeholder="Enter API secret"
                      className="w-full h-10 border-[#111111]/10 rounded-none font-mono text-sm pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecret(!showSecret)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555555] hover:text-[#111111]"
                    >
                      {showSecret ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <button
                  data-testid="save-api-key-btn"
                  type="submit"
                  disabled={savingKey}
                  className="px-4 h-10 btn-primary flex items-center gap-2 disabled:opacity-50"
                >
                  {savingKey ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Plus size={18} />
                      Save API Key
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Trading Pairs Section */}
        <div className="bg-white border border-[#111111]/10" data-testid="trading-pairs-section">
          <div className="panel-header flex items-center gap-2">
            <ListChecks size={18} />
            <span className="font-medium">Trading Pairs</span>
          </div>
          <div className="p-4">
            <p className="text-sm text-[#555555] mb-4">
              Select the cryptocurrency pairs you want to trade. The bot will monitor these pairs
              for trading opportunities.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {availablePairs.map((pair) => (
                <label
                  key={pair}
                  className={`flex items-center gap-2 p-3 border cursor-pointer transition-colors ${
                    tradingPairs.includes(pair)
                      ? "border-[#002FA7] bg-[#002FA7]/5"
                      : "border-[#111111]/10 hover:border-[#111111]/20"
                  }`}
                >
                  <Checkbox
                    data-testid={`pair-${pair}`}
                    checked={tradingPairs.includes(pair)}
                    onCheckedChange={() => handlePairToggle(pair)}
                  />
                  <span className="text-sm font-medium">{pair}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* TradingView Webhook Section */}
        <div className="bg-white border border-[#111111]/10" data-testid="webhook-section">
          <div className="panel-header flex items-center gap-2">
            <Lightning size={18} className="text-amber-500" />
            <span className="font-medium">TradingView.to Webhook</span>
            <span className="ml-auto text-xs px-2 py-0.5 bg-amber-50 text-amber-600 border border-amber-200">
              BYBIT GEO-BYPASS
            </span>
          </div>
          <div className="p-4">
            <div className="bg-blue-50 border border-blue-200 p-3 mb-4">
              <p className="text-xs text-blue-800">
                <strong>Why use this?</strong> Bybit API is geo-restricted from our cloud servers. 
                TradingView.to acts as a relay service to execute trades on Bybit without restrictions.
                Your API keys are stored securely on TradingView.to's side.
              </p>
            </div>

            {/* Current Webhook Status */}
            {webhookConfig?.configured ? (
              <div className="mb-6">
                <p className="data-label mb-3">Current Configuration</p>
                <div className="flex items-center justify-between p-3 bg-[#F4F4F0] border border-[#111111]/10">
                  <div>
                    <div className="flex items-center gap-2">
                      <CheckCircle size={16} className="text-emerald-500" />
                      <p className="font-medium text-sm">
                        {webhookConfig.exchange?.toUpperCase()} Webhook
                      </p>
                    </div>
                    <p className="text-xs text-[#555555] font-mono mt-1">
                      {webhookConfig.webhook_url_masked}
                    </p>
                    {webhookConfig.updated_at && (
                      <p className="text-[10px] text-[#999] mt-1">
                        Configured: {new Date(webhookConfig.updated_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 text-xs ${
                      webhookConfig.enabled 
                        ? "bg-emerald-50 text-emerald-600" 
                        : "bg-gray-100 text-gray-500"
                    }`}>
                      {webhookConfig.enabled ? "ENABLED" : "DISABLED"}
                    </span>
                    <button
                      data-testid="test-webhook-btn"
                      onClick={handleTestWebhook}
                      disabled={testingWebhook}
                      className="px-2 py-1 text-xs bg-blue-50 text-blue-600 hover:bg-blue-100"
                    >
                      {testingWebhook ? "Testing..." : "Test"}
                    </button>
                    <button
                      data-testid="delete-webhook-btn"
                      onClick={handleDeleteWebhook}
                      className="p-1.5 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-4 flex items-center gap-2 p-3 bg-amber-50 border border-amber-200">
                <Warning size={16} className="text-amber-600" />
                <p className="text-xs text-amber-800">
                  No webhook configured. Add your TradingView.to webhook URL to enable Bybit trading.
                </p>
              </div>
            )}

            {/* Add Webhook Form */}
            <form onSubmit={handleSaveWebhook} className="space-y-4">
              <div>
                <label className="data-label mb-2 block">TradingView.to Webhook URL</label>
                <Input
                  data-testid="webhook-url-input"
                  type="url"
                  placeholder="https://tradingview.to/bybit/your-webhook-id"
                  value={webhookForm.webhook_url}
                  onChange={(e) => setWebhookForm({ ...webhookForm, webhook_url: e.target.value })}
                  className="w-full h-10 border-[#111111]/10 rounded-none font-mono text-sm"
                />
                <p className="text-[10px] text-[#999] mt-1">
                  Get your webhook URL from <a href="https://tradingview.to" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">tradingview.to</a> after connecting your Bybit account
                </p>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    data-testid="webhook-enabled-checkbox"
                    checked={webhookForm.enabled}
                    onCheckedChange={(checked) => setWebhookForm({ ...webhookForm, enabled: checked })}
                  />
                  <span className="text-sm">Enable webhook trading</span>
                </div>
              </div>

              <button
                data-testid="save-webhook-btn"
                type="submit"
                disabled={savingWebhook || !webhookForm.webhook_url}
                className="px-4 h-10 btn-primary flex items-center gap-2 disabled:opacity-50"
              >
                {savingWebhook ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Lightning size={18} />
                    Save Webhook
                  </>
                )}
              </button>
            </form>

            {/* Manual Balance Entry */}
            {webhookConfig?.configured && (
              <div className="mt-6 pt-6 border-t border-[#111111]/10">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <CurrencyDollar size={16} className="text-[#002FA7]" />
                    <p className="data-label">Bybit Balance</p>
                  </div>
                  <button
                    data-testid="fetch-balance-btn"
                    onClick={handleFetchBalance}
                    disabled={fetchingBalance}
                    className="px-3 py-1.5 text-xs bg-[#002FA7] text-white hover:bg-[#002FA7]/90 flex items-center gap-2 disabled:opacity-50"
                  >
                    {fetchingBalance ? (
                      <>
                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Fetching...
                      </>
                    ) : (
                      <>
                        <ArrowsClockwise size={14} />
                        Update Balance
                      </>
                    )}
                  </button>
                </div>
                <p className="text-[10px] text-[#999] mb-3">
                  Click "Update Balance" to fetch from Bybit via webhook, or enter manually below.
                </p>
                <form onSubmit={handleSaveManualBalance} className="flex gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999]">$</span>
                    <Input
                      data-testid="manual-balance-input"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="1000.00"
                      value={bybitBalance}
                      onChange={(e) => setBybitBalance(e.target.value)}
                      className="pl-7 h-10 border-[#111111]/10 rounded-none"
                    />
                  </div>
                  <button
                    data-testid="save-balance-btn"
                    type="submit"
                    disabled={savingBalance || !bybitBalance}
                    className="px-4 h-10 btn-secondary flex items-center gap-2 disabled:opacity-50"
                  >
                    {savingBalance ? (
                      <div className="w-4 h-4 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
                    ) : (
                      "Save"
                    )}
                  </button>
                </form>
                {manualBalances?.bybit && (
                  <p className="text-[10px] text-emerald-600 mt-2">
                    Last updated: {new Date(manualBalances.bybit.updated_at).toLocaleString()}
                    {manualBalances.bybit.source === 'webhook_fetch' && ' (via webhook)'}
                  </p>
                )}
                {lastFetchedBalance && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 text-xs space-y-1">
                    <p className="font-medium text-blue-800">TTA Response:</p>
                    {lastFetchedBalance.raw_text && lastFetchedBalance.raw_text !== 'Created' ? (
                      <pre className="text-blue-700 whitespace-pre-wrap font-mono text-[10px]">
                        {lastFetchedBalance.raw_text}
                      </pre>
                    ) : (
                      <p className="text-blue-600">
                        {JSON.stringify(lastFetchedBalance.raw_response || {}).slice(0, 150)}
                      </p>
                    )}
                    {lastFetchedBalance.all_balances && Object.keys(lastFetchedBalance.all_balances).length > 0 && (
                      <div className="mt-2 pt-2 border-t border-blue-200">
                        <p className="font-medium text-blue-800">Parsed Balances:</p>
                        {Object.entries(lastFetchedBalance.all_balances).map(([coin, value]) => (
                          <p key={coin} className="text-blue-700">
                            {coin}: {Number(value).toLocaleString('en-US', {minimumFractionDigits: 2})}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Balance Management */}
        <div className="bg-white border border-[#111111]/10" data-testid="balance-section">
          <div className="panel-header flex items-center gap-2">
            <CurrencyDollar size={18} />
            <span className="font-medium">Balance Management</span>
          </div>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Current Balance</p>
                <p className="text-2xl font-bold mt-1">${user?.balance?.toFixed(2)}</p>
                <p className="text-xs text-[#555555] mt-1">
                  Initial: ${user?.initial_balance?.toFixed(2)}
                </p>
              </div>
              <button
                data-testid="reset-balance-btn"
                onClick={handleResetBalance}
                className="px-4 h-10 flex items-center gap-2 border border-[#111111]/10 hover:bg-[#F4F4F0] transition-colors"
              >
                <ArrowsClockwise size={18} />
                Reset to $1,000
              </button>
            </div>
          </div>
        </div>

        {/* Trading Parameters Info */}
        <div className="bg-white border border-[#111111]/10" data-testid="params-section">
          <div className="panel-header">
            <span className="font-medium">Trading Parameters</span>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="p-3 bg-[#F4F4F0] border border-[#111111]/10">
                <p className="data-label mb-1">Risk Per Trade</p>
                <p className="font-bold">10% of Equity</p>
              </div>
              <div className="p-3 bg-[#F4F4F0] border border-[#111111]/10">
                <p className="data-label mb-1">Circuit Breaker</p>
                <p className="font-bold">$500</p>
              </div>
              <div className="p-3 bg-[#F4F4F0] border border-[#111111]/10">
                <p className="data-label mb-1">BTC Stop Loss</p>
                <p className="font-bold">2.5% (~4x Leverage)</p>
              </div>
              <div className="p-3 bg-[#F4F4F0] border border-[#111111]/10">
                <p className="data-label mb-1">ETH Stop Loss</p>
                <p className="font-bold">3.5% (~2.9x Leverage)</p>
              </div>
              <div className="p-3 bg-[#F4F4F0] border border-[#111111]/10">
                <p className="data-label mb-1">SOL Stop Loss</p>
                <p className="font-bold">8.2% (~1.25x Leverage)</p>
              </div>
              <div className="p-3 bg-[#F4F4F0] border border-[#111111]/10">
                <p className="data-label mb-1">Take Profit 1</p>
                <p className="font-bold">2.5:1 R:R (50%)</p>
              </div>
              <div className="p-3 bg-[#F4F4F0] border border-[#111111]/10">
                <p className="data-label mb-1">Trailing Stop</p>
                <p className="font-bold">BTC: 1.5% | ETH: 2% | SOL: 4%</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
